/**
 * Förberedelseplaner – återanvändbara mallar, flerstegsplanering, granskning/godkännande, livscykel och
 * automatiska UTKAST. Ren, deterministisk domänlogik med injicerad klocka; ingen extern tjänst.
 * Beteende, tidsregler och protokoll: docs/PREPARATION-PLANS.md.
 *
 * Grundprinciper:
 *  - Upptagen tid definieras på ETT ställe: busyIntervals i prep.ts (samma regler som enstaka förberedelser).
 *  - Förslagsmotorn skapar, flyttar, raderar eller klarmarkerar ALDRIG något. Bara ett uttryckligt
 *    godkännande (buildPlanCommit) ändrar kalendern, och det kontrollerar hela underlaget igen.
 *  - Planen (PrepPlan) bär relationen deadline → steg → kalenderpass. Passen är vanliga LocalEvent med
 *    linkedSourceKey; kopior av ett pass får aldrig planens identitet.
 *  - Identiteter är deterministiska: plan per källpost, regel per källa, pass per (plan, generation, steg,
 *    instans). Ett omförsök, en annan enhet eller en SQL-retry kan därför aldrig skapa nya identiteter.
 */
import type { AppState, ISODate, ISOInstant, LocalEvent, PlainDateTimeStr, PrepPlan, PrepPlanBasis, PrepPlanStep, PrepRule, PrepTemplate, PrepTemplateContent, SourceItem, Weekday } from './model.ts';
import { Temporal, addDays, compareDates, daysBetween, resolveLocal, timingInstants } from './time.ts';
import { canonicalJson, sha256Text } from './hash.ts';
import { commit, derivedId, exportUidFor, iso, NotFoundError, ValidationError, type Change, type OpResult } from './ops.ts';
import { busyIntervals, deadlineStart, isRecurringSource, MAX_LOOKAHEAD_DAYS, type Busy, type BusyResult } from './prep.ts';
import { isDeadline } from './deadlines.ts';

// ---------------------------------------------------------------------------
// Gränser och identiteter
// ---------------------------------------------------------------------------

export const PREP_MAX_STEPS = 5;
export const PREP_MIN_DURATION = 15;
export const PREP_MAX_DURATION = 240;
export const PREP_MIN_DAYS_BEFORE = 1;
export const PREP_MAX_DAYS_BEFORE = 14;
/** Planering och automatiska förslag: högst 21 kalenderdagar före respektive deadline. */
export const PREP_HORIZON_DAYS = MAX_LOOKAHEAD_DAYS;
/** Högst så många automatiska utkast beräknas per omräkning (närmaste deadlines först). */
export const MAX_AUTO_DRAFTS = 20;
/** Befintlig påminnelsepolicy för enstaka förberedelser (createLinkedWork): 60 min före. */
export const PREP_ALARM_OFFSET_MINUTES = -60;
const STEP_MS = 15 * 60_000;

/** En plan per källdeadline: ID:t härleds ur källnyckeln (aldrig ur titeln). */
export function prepPlanIdFor(sourceKey: string): string {
  return 'pplan_' + sha256Text('prep-plan|' + sourceKey).slice(0, 24);
}
/** Högst en regel per importkälla. */
export function prepRuleIdFor(sourceId: string): string {
  return 'prule_' + sha256Text('prep-rule|' + sourceId).slice(0, 24);
}
/** Kalenderpassets ID: samma på alla enheter och vid varje omförsök. */
export function prepEventIdFor(planId: string, generation: number, stepId: string, instance: number): string {
  return derivedId('ev', `${planId}:${generation}:${stepId}`, instance);
}

// ---------------------------------------------------------------------------
// Fel
// ---------------------------------------------------------------------------

export type PlanStaleReason =
  | 'time-passed'
  | 'deadline-changed'
  | 'zone-changed'
  | 'source-changed'
  | 'source-gone'
  | 'source-review'
  | 'deadline-done'
  | 'not-deadline'
  | 'recurring'
  | 'template-changed'
  | 'template-deleted'
  | 'calendar-invalid'
  | 'plan-changed'
  | 'plan-exists'
  | 'event-changed'
  | 'slot-taken'
  | 'singles-exist'
  | 'invalid';

const STALE_TEXT: Record<PlanStaleReason, string> = {
  'time-passed': 'Tiden har gått – förslaget gäller inte längre. Se nytt förslag.',
  'deadline-changed': 'Deadlinens datum eller tid har ändrats. Se nytt förslag.',
  'zone-changed': 'Tidszonen har ändrats. Se nytt förslag.',
  'source-changed': 'Källposten har ändrats sedan förslaget gjordes. Se nytt förslag.',
  'source-gone': 'Källposten finns inte längre eller är dold.',
  'source-review': 'Källposten behöver granskas först.',
  'deadline-done': 'Deadlinen är markerad klar här.',
  'not-deadline': 'Posten räknas inte längre som deadline.',
  recurring: 'Återkommande källdatum stöds inte av förberedelseplaner ännu.',
  'template-changed': 'Mallen har ändrats sedan förslaget gjordes. Se nytt förslag.',
  'template-deleted': 'Mallen har tagits bort. Välj en annan mall.',
  'calendar-invalid': 'Målkalendern finns inte eller är inte skrivbar.',
  'plan-changed': 'Planen har ändrats på en annan enhet eller i en annan flik. Se nytt förslag.',
  'plan-exists': 'Det finns redan en förberedelseplan för den här deadlinen.',
  'event-changed': 'Ett pass har ändrats sedan förslaget gjordes. Se nytt förslag.',
  'slot-taken': 'En av tiderna är inte längre ledig. Se nytt förslag.',
  'singles-exist': 'Det finns redan förberedelser för den här deadlinen. Välj uttryckligen om planen ska läggas till.',
  invalid: 'Förslaget är ogiltigt. Se nytt förslag.',
};

/** Godkännandet avvisades: underlaget har ändrats. Ingenting har skapats eller ändrats. */
export class PlanStaleError extends ValidationError {
  readonly reason: PlanStaleReason;
  constructor(reason: PlanStaleReason, detail?: string) {
    super(STALE_TEXT[reason] + (detail ? ` (${detail})` : ''));
    this.name = 'PlanStaleError';
    this.reason = reason;
  }
}

// ---------------------------------------------------------------------------
// Mallar: validering och startmall
// ---------------------------------------------------------------------------

export type TemplateIssueCode =
  | 'name'
  | 'steps-count'
  | 'step-title'
  | 'step-duration'
  | 'step-days'
  | 'step-order'
  | 'step-ids'
  | 'day-count'
  | 'weekdays'
  | 'window'
  | 'window-short'
  | 'calendar';

export interface TemplateIssue {
  code: TemplateIssueCode;
  message: string;
  stepIndex?: number;
}

const HHMM = /^([01]\d|2[0-3]):([0-5]\d)$/;
const toMin = (s: string) => Number(s.slice(0, 2)) * 60 + Number(s.slice(3, 5));
const pad2 = (n: number) => String(n).padStart(2, '0');
const minToHhmm = (m: number) => `${pad2(Math.floor(m / 60))}:${pad2(m % 60)}`;

/** Första felet i mallens innehåll (svensk text + kod för översättning), eller null. */
export function validatePrepTemplateContent(state: Pick<AppState, 'calendars'> | null, c: PrepTemplateContent): TemplateIssue | null {
  if (!c || typeof c !== 'object') return { code: 'name', message: 'Mallen saknar innehåll.' };
  const name = typeof c.name === 'string' ? c.name.trim() : '';
  if (!name || name.length > 80) return { code: 'name', message: 'Ge mallen ett namn (högst 80 tecken).' };
  if (!Array.isArray(c.steps) || c.steps.length < 1 || c.steps.length > PREP_MAX_STEPS) return { code: 'steps-count', message: `En mall har 1–${PREP_MAX_STEPS} steg.` };
  const ids = new Set<string>();
  let longest = 0;
  for (let i = 0; i < c.steps.length; i++) {
    const s = c.steps[i]!;
    const title = typeof s?.title === 'string' ? s.title.trim() : '';
    if (!title || title.length > 120) return { code: 'step-title', message: `Steg ${i + 1} behöver en titel (högst 120 tecken).`, stepIndex: i };
    if (!Number.isInteger(s.durationMinutes) || s.durationMinutes < PREP_MIN_DURATION || s.durationMinutes > PREP_MAX_DURATION || s.durationMinutes % 5 !== 0) {
      return { code: 'step-duration', message: `Steg ${i + 1}: längden ska vara ${PREP_MIN_DURATION}–${PREP_MAX_DURATION} minuter.`, stepIndex: i };
    }
    if (!Number.isInteger(s.daysBefore) || s.daysBefore < PREP_MIN_DAYS_BEFORE || s.daysBefore > PREP_MAX_DAYS_BEFORE) {
      return { code: 'step-days', message: `Steg ${i + 1}: dagar före deadline ska vara ${PREP_MIN_DAYS_BEFORE}–${PREP_MAX_DAYS_BEFORE}.`, stepIndex: i };
    }
    if (i > 0 && s.daysBefore > c.steps[i - 1]!.daysBefore) {
      return { code: 'step-order', message: `Steg ${i + 1} kan inte ligga fler dagar före deadline än steget före. Stegen står i tidsordning.`, stepIndex: i };
    }
    if (typeof s.id !== 'string' || !s.id || s.id.length > 64 || ids.has(s.id)) return { code: 'step-ids', message: 'Stegen saknar unika identiteter.', stepIndex: i };
    ids.add(s.id);
    longest = Math.max(longest, s.durationMinutes);
  }
  if (c.dayCount !== 'weekdays' && c.dayCount !== 'calendar') return { code: 'day-count', message: 'Välj om dagarna räknas som vardagar eller kalenderdagar.' };
  if (!Array.isArray(c.weekdays) || !c.weekdays.length || c.weekdays.some((d) => !Number.isInteger(d) || d < 1 || d > 7) || new Set(c.weekdays).size !== c.weekdays.length) {
    return { code: 'weekdays', message: 'Välj minst en tillåten veckodag.' };
  }
  const w = c.window;
  if (!w || !HHMM.test(w.start ?? '') || !HHMM.test(w.end ?? '') || toMin(w.start) % 15 || toMin(w.end) % 15 || toMin(w.end) <= toMin(w.start)) {
    return { code: 'window', message: 'Tidsperioden ska vara hela kvartar och sluta efter att den börjar (samma dag).' };
  }
  if (toMin(w.end) - toMin(w.start) < longest) return { code: 'window-short', message: 'Tidsperioden är kortare än det längsta steget.' };
  if (state) {
    const cal = state.calendars[c.calendarId];
    if (!cal || cal.kind === 'import') return { code: 'calendar', message: 'Välj en egen kalender (Privat eller Arbete). Importerade kalendrar är skrivskyddade.' };
  } else if (typeof c.calendarId !== 'string' || !c.calendarId) return { code: 'calendar', message: 'Välj en egen kalender.' };
  return null;
}

/**
 * Den valfria startmallen "Förbered i två steg". Skapar INGEN data – den blir en vanlig, egen mall först
 * när användaren väljer den (createPrepTemplate). Texten följer språket vid skapandet och översätts
 * aldrig i efterhand (den är användarens egen text från och med då).
 */
export function starterPrepTemplate(lang: 'sv' | 'en', calendarId = 'cal-arbete'): PrepTemplateContent {
  const sv = lang === 'sv';
  return {
    name: sv ? 'Förbered i två steg' : 'Prepare in two steps',
    steps: [
      { id: '', title: sv ? 'Samla underlag' : 'Gather material', durationMinutes: 45, daysBefore: 3 },
      { id: '', title: sv ? 'Gå igenom' : 'Review', durationMinutes: 45, daysBefore: 1 },
    ],
    dayCount: 'weekdays',
    window: { start: '08:00', end: '17:00' },
    weekdays: [1, 2, 3, 4, 5],
    calendarId,
  };
}

function normalizeContent(c: PrepTemplateContent, opId: string, keepIds: ReadonlySet<string>): PrepTemplateContent {
  return {
    name: c.name.trim(),
    steps: c.steps.map((s, i) => ({
      id: s.id && keepIds.has(s.id) ? s.id : derivedId('pst', opId, i),
      title: s.title.trim(),
      durationMinutes: s.durationMinutes,
      daysBefore: s.daysBefore,
    })),
    dayCount: c.dayCount,
    window: { start: c.window.start, end: c.window.end },
    weekdays: [...new Set(c.weekdays)].sort((a, b) => a - b) as Weekday[],
    calendarId: c.calendarId,
  };
}

function templateError(state: AppState, c: PrepTemplateContent): void {
  const issue = validatePrepTemplateContent(state, c);
  if (issue) throw new ValidationError(issue.message);
}

/** Planändringar ångras inte via den allmänna ångra-funktionen (planer har egna, synliga flöden). */
function commitPrep(state: AppState, opId: string, label: string, change: Change, now: Temporal.Instant): OpResult {
  const r = commit(state, opId, label, change, now);
  return { ...r, undo: null };
}

export function createPrepTemplate(state: AppState, opId: string, content: PrepTemplateContent, now: Temporal.Instant): OpResult & { id: string } {
  // Nya steg (utan befintligt ID) får härledda ID:n så att samma opId alltid ger samma mall.
  const normalized = normalizeContent({ ...content, steps: content.steps.map((s) => ({ ...s, id: '' })) }, opId, new Set());
  templateError(state, normalized);
  const id = derivedId('ptpl', opId, 0);
  const t: PrepTemplate = { ...normalized, id, revision: 1, createdAt: iso(now), updatedAt: iso(now) };
  return { ...commitPrep(state, opId, `Skapa mall "${t.name}"`, { prepTemplates: { [id]: t } }, now), id };
}

/** Redigering ändrar ALDRIG befintliga planer eller pass (de har en egen ögonblicksbild). */
export function updatePrepTemplate(state: AppState, opId: string, id: string, content: PrepTemplateContent, now: Temporal.Instant, expectedRevision?: number): OpResult {
  const cur = state.prepTemplates?.[id];
  if (!cur) throw new NotFoundError('Mallen');
  if (expectedRevision !== undefined && cur.revision !== expectedRevision) throw new ValidationError('Mallen har ändrats på en annan enhet eller i en annan flik. Öppna den igen.');
  const normalized = normalizeContent(content, opId, new Set(cur.steps.map((s) => s.id)));
  templateError(state, normalized);
  const next: PrepTemplate = { ...cur, ...normalized, revision: cur.revision + 1, updatedAt: iso(now) };
  return commitPrep(state, opId, `Ändra mall "${next.name}"`, { prepTemplates: { [id]: next } }, now);
}

/**
 * Tar bort mallen. Godkända planer behåller sin ögonblicksbild och sina pass. Regler som använder
 * mallen pausas (pausedReason 'template-deleted') så att inga nya förslag skapas från en borttagen mall.
 */
export function deletePrepTemplate(state: AppState, opId: string, id: string, now: Temporal.Instant): OpResult & { pausedRules: string[] } {
  const cur = state.prepTemplates?.[id];
  if (!cur) throw new NotFoundError('Mallen');
  const rules: Record<string, PrepRule> = {};
  for (const r of Object.values(state.prepRules ?? {})) {
    if (r.templateId !== id) continue;
    rules[r.id] = { ...r, status: 'paused', pausedReason: 'template-deleted', revision: r.revision + 1, updatedAt: iso(now) };
  }
  const res = commitPrep(state, opId, `Ta bort mall "${cur.name}"`, { prepTemplates: { [id]: null }, ...(Object.keys(rules).length ? { prepRules: rules } : {}) }, now);
  return { ...res, pausedRules: Object.keys(rules) };
}

// ---------------------------------------------------------------------------
// Regler (opt-in per importkälla)
// ---------------------------------------------------------------------------

/** Skapar eller ändrar källans enda regel. Av som standard – anropas bara efter ett uttryckligt val. */
export function setPrepRule(state: AppState, opId: string, input: { sourceId: string; templateId: string; status: 'active' | 'paused' }, now: Temporal.Instant): OpResult & { id: string } {
  if (!state.sources[input.sourceId]) throw new NotFoundError('Importkällan');
  if (!state.prepTemplates?.[input.templateId]) throw new NotFoundError('Mallen');
  const id = prepRuleIdFor(input.sourceId);
  const cur = state.prepRules?.[id];
  const next: PrepRule = cur
    ? { ...cur, templateId: input.templateId, status: input.status, pausedReason: input.status === 'paused' ? 'user' : null, revision: cur.revision + 1, updatedAt: iso(now) }
    : { id, sourceId: input.sourceId, templateId: input.templateId, status: input.status, pausedReason: input.status === 'paused' ? 'user' : null, revision: 1, createdAt: iso(now), updatedAt: iso(now) };
  return { ...commitPrep(state, opId, input.status === 'active' ? 'Automatiska förslag på' : 'Automatiska förslag pausade', { prepRules: { [id]: next } }, now), id };
}

export function setPrepRuleStatus(state: AppState, opId: string, ruleId: string, status: 'active' | 'paused', now: Temporal.Instant): OpResult {
  const cur = state.prepRules?.[ruleId];
  if (!cur) throw new NotFoundError('Regeln');
  if (status === 'active' && !state.prepTemplates?.[cur.templateId]) throw new ValidationError('Mallen för regeln finns inte längre. Välj en annan mall först.');
  const next: PrepRule = { ...cur, status, pausedReason: status === 'paused' ? 'user' : null, revision: cur.revision + 1, updatedAt: iso(now) };
  return commitPrep(state, opId, status === 'active' ? 'Automatiska förslag på' : 'Automatiska förslag pausade', { prepRules: { [ruleId]: next } }, now);
}

/** Regeln tas bort. Redan bokade pass och godkända planer ligger kvar. */
export function deletePrepRule(state: AppState, opId: string, ruleId: string, now: Temporal.Instant): OpResult {
  if (!state.prepRules?.[ruleId]) throw new NotFoundError('Regeln');
  return commitPrep(state, opId, 'Regel borttagen', { prepRules: { [ruleId]: null } }, now);
}

/** Pausar alla regler (vid återställning från säkerhetskopia eller överföring från gästläget). */
export function pauseAllPrepRules(state: AppState, reason: 'restored' | 'transferred'): AppState {
  const rules = state.prepRules ?? {};
  if (!Object.keys(rules).length) return state;
  const next: Record<string, PrepRule> = {};
  for (const [k, r] of Object.entries(rules)) next[k] = r.status === 'paused' && r.pausedReason ? r : { ...r, status: 'paused', pausedReason: reason, revision: r.revision + 1 };
  return { ...state, prepRules: next };
}

// ---------------------------------------------------------------------------
// Avfärdade förslag (beständigt i överlägget)
// ---------------------------------------------------------------------------

/** Avfärda förslaget för det här datumet. Gäller över omladdning, synk och oförändrad återimport. */
export function dismissPrepSuggestion(state: AppState, opId: string, sourceKey: string, now: Temporal.Instant): OpResult {
  const src = state.sourceItems[sourceKey];
  if (!src) throw new NotFoundError('Den importerade posten');
  const dl = deadlineStart(src, state.settings.timeZone);
  const ov = state.overlays[sourceKey] ?? { key: sourceKey, revision: 0 };
  const next = { ...ov, prepDismissed: { at: iso(now), deadlineStart: iso(Temporal.Instant.fromEpochMilliseconds(dl.ms)) }, revision: ov.revision + 1 };
  return commitPrep(state, opId, 'Förslaget avfärdat', { overlays: { [sourceKey]: next } }, now);
}

/** Uttrycklig återupptagning: visa förslaget igen. */
export function restorePrepSuggestion(state: AppState, opId: string, sourceKey: string, now: Temporal.Instant): OpResult {
  const ov = state.overlays[sourceKey];
  if (!ov?.prepDismissed) throw new ValidationError('Förslaget är inte avfärdat.');
  return commitPrep(state, opId, 'Förslaget visas igen', { overlays: { [sourceKey]: { ...ov, prepDismissed: null, revision: ov.revision + 1 } } }, now);
}

/**
 * Är förslaget avfärdat för deadlinens NUVARANDE datum? Ett ändrat källdatum är en ny förberedelsesituation
 * och återupptar förslaget; rubrik, anteckningar, klarstatus eller pausade regler gör det inte.
 */
export function isPrepDismissed(state: AppState, sourceKey: string): boolean {
  const d = state.overlays[sourceKey]?.prepDismissed;
  const src = state.sourceItems[sourceKey];
  if (!d || !src) return false;
  return Temporal.Instant.from(d.deadlineStart).epochMilliseconds === deadlineStart(src, state.settings.timeZone).ms;
}

// ---------------------------------------------------------------------------
// Planindex, befintliga förberedelser
// ---------------------------------------------------------------------------

export function planForSource(state: AppState, sourceKey: string): PrepPlan | undefined {
  return state.prepPlans?.[prepPlanIdFor(sourceKey)];
}

/** event-ID → plan och steg. Bara planens EGNA steg; en kopia av ett pass finns aldrig här. */
export function planEventIndex(state: AppState): Map<string, { planId: string; stepId: string; active: boolean }> {
  const m = new Map<string, { planId: string; stepId: string; active: boolean }>();
  for (const p of Object.values(state.prepPlans ?? {})) for (const s of p.steps) m.set(s.eventId, { planId: p.id, stepId: s.stepId, active: p.status === 'active' });
  return m;
}

export interface ExistingPrep {
  id: string;
  title: string;
  start: PlainDateTimeStr | ISODate;
  end: PlainDateTimeStr | ISODate;
  allDay: boolean;
  timeZone: string | null;
  done: boolean;
  /** 'single' = enstaka förberedelse; 'ended-plan' = pass kvar från en avslutad plan. */
  kind: 'single' | 'ended-plan';
}

/** Förberäknat index för många anrop i följd (automatiska utkast). */
export interface PrepLinkIndex {
  plans: Map<string, { planId: string; stepId: string; active: boolean }>;
  bySource: Map<string, LocalEvent[]>;
}
export function prepLinkIndex(state: AppState): PrepLinkIndex {
  const bySource = new Map<string, LocalEvent[]>();
  for (const e of Object.values(state.events)) {
    if (e.deleted || !e.linkedSourceKey) continue;
    const l = bySource.get(e.linkedSourceKey);
    if (l) l.push(e);
    else bySource.set(e.linkedSourceKey, [e]);
  }
  return { plans: planEventIndex(state), bySource };
}

/** Länkade förberedelser för deadlinen som INTE hör till en aktiv plan. Adopteras aldrig tyst. */
export function existingPrepOutsidePlan(state: AppState, sourceKey: string, index?: PrepLinkIndex): ExistingPrep[] {
  const idx = index?.plans ?? planEventIndex(state);
  const linked = index ? (index.bySource.get(sourceKey) ?? []) : Object.values(state.events).filter((e) => !e.deleted && e.linkedSourceKey === sourceKey);
  const out: ExistingPrep[] = [];
  for (const e of linked) {
    if (e.deleted || e.linkedSourceKey !== sourceKey) continue;
    const owner = idx.get(e.id);
    if (owner?.active) continue;
    out.push({
      id: e.id,
      title: e.title,
      start: e.timing.start,
      end: e.timing.kind === 'allDay' ? e.timing.endExclusive : e.timing.end,
      allDay: e.timing.kind === 'allDay',
      timeZone: e.timing.kind === 'timed' ? e.timing.timeZone : null,
      done: !!e.done,
      kind: owner ? 'ended-plan' : 'single',
    });
  }
  return out.sort((a, b) => (a.start < b.start ? -1 : a.start > b.start ? 1 : 0));
}

// ---------------------------------------------------------------------------
// Källkontroller (samma för förslag, godkännande och automatiska utkast)
// ---------------------------------------------------------------------------

export type SourceBlock = 'not-found' | 'archived' | 'missing' | 'review' | 'not-deadline' | 'recurring' | 'passed' | 'done';

function sourceBlock(state: AppState, src: SourceItem | undefined, nowMs: number): SourceBlock | null {
  if (!src) return 'not-found';
  if (src.archived) return 'archived';
  if (src.presence === 'missing') return 'missing';
  if (state.overlays[src.key]?.review) return 'review';
  if (isRecurringSource(state, src)) return 'recurring';
  if (!isDeadline(state, { ref: { kind: 'source', key: src.key } })) return 'not-deadline';
  if (state.overlays[src.key]?.done) return 'done';
  if (deadlineStart(src, state.settings.timeZone).ms <= nowMs) return 'passed';
  return null;
}

const BLOCK_TO_STALE: Record<SourceBlock, PlanStaleReason> = {
  'not-found': 'source-gone',
  archived: 'source-gone',
  missing: 'source-review',
  review: 'source-review',
  'not-deadline': 'not-deadline',
  recurring: 'recurring',
  passed: 'time-passed',
  done: 'deadline-done',
};

export function currentBasis(state: AppState, src: SourceItem): PrepPlanBasis {
  const tz = state.settings.timeZone;
  const dl = deadlineStart(src, tz);
  return {
    sourceKey: src.key,
    deadlineStart: iso(Temporal.Instant.fromEpochMilliseconds(dl.ms)),
    deadlineDate: dl.date,
    allDay: src.data.timing.kind === 'allDay',
    sourceHash: src.contentHash,
    timeZone: tz,
  };
}

// ---------------------------------------------------------------------------
// Tidsrutnät: kvartar i tillåten period, sommartid hanteras uttryckligen
// ---------------------------------------------------------------------------

interface Slot {
  startMs: number;
  endMs: number;
  start: PlainDateTimeStr;
  end: PlainDateTimeStr;
  date: ISODate;
}

interface Grid {
  tz: string;
  window: { start: number; end: number };
  weekdays: Set<number>;
  /** Tidigaste tillåtna start (nu avrundat uppåt till kvart, och sökfönstrets första dag). */
  lowerMs: number;
  lowerDate: ISODate;
  deadlineMs: number;
  deadlineDate: ISODate;
  busy: Busy[];
  cache: Map<string, Slot[]>;
  dayInfo: Map<string, { startMs: number; uniform: boolean }>;
}

const ceil15 = (ms: number) => Math.ceil(ms / STEP_MS) * STEP_MS;

function wallOf(ms: number, tz: string): PlainDateTimeStr {
  return Temporal.Instant.fromEpochMilliseconds(ms).toZonedDateTimeISO(tz).toPlainDateTime().toString({ smallestUnit: 'minute' });
}
function dateOfMs(ms: number, tz: string): ISODate {
  return Temporal.Instant.fromEpochMilliseconds(ms).toZonedDateTimeISO(tz).toPlainDate().toString();
}
function dow(d: ISODate): number {
  return Temporal.PlainDate.from(d).dayOfWeek;
}

/** Dagens första ögonblick och om dagen är "jämn" (24 h utan sommartidsomställning). */
function dayStart(g: Grid, d: ISODate): { startMs: number; uniform: boolean } {
  const hit = g.dayInfo.get(d);
  if (hit) return hit;
  const a = resolveLocal(`${d}T00:00`, g.tz, 'compatible');
  const b = resolveLocal(`${addDays(d, 1)}T00:00`, g.tz, 'compatible');
  const startMs = a.zdt.epochMilliseconds;
  const info = { startMs, uniform: !a.adjusted && !b.adjusted && b.zdt.epochMilliseconds - startMs === 86_400_000 };
  g.dayInfo.set(d, info);
  return info;
}

/**
 * Alla lediga pass med längden dur på dagen d, i tidsordning. Ett pass godtas bara om start och slut finns
 * och är entydiga i zonen och om väggklockans längd = faktisk längd (ett 45-minuterspass blir aldrig 105
 * minuter över en omställning). Upptagen tid och "aldrig före nu"/"före deadline" kontrolleras här.
 */
function daySlots(g: Grid, d: ISODate, dur: number): Slot[] {
  const key = `${d}|${dur}`;
  const hit = g.cache.get(key);
  if (hit) return hit;
  const out: Slot[] = [];
  if (g.weekdays.has(dow(d)) && compareDates(d, g.lowerDate) >= 0 && compareDates(d, g.deadlineDate) <= 0) {
    const info = dayStart(g, d);
    const dayEndMs = info.startMs + 26 * 3_600_000;
    const dayBusy = g.busy.filter((b) => b.start < dayEndMs && b.end > info.startMs - 3_600_000);
    for (let m = g.window.start; m + dur <= g.window.end; m += 15) {
      let startMs: number;
      if (info.uniform) startMs = info.startMs + m * 60_000;
      else {
        const wall = `${d}T${minToHhmm(m)}`;
        const r = resolveLocal(wall, g.tz, 'compatible');
        if (r.adjusted) continue; // klockslaget finns inte (vårens glapp)
        startMs = r.zdt.epochMilliseconds;
        const later = Temporal.PlainDateTime.from(wall).toZonedDateTime(g.tz, { disambiguation: 'later' }).epochMilliseconds;
        if (later !== startMs) continue; // dubbeltimme (hösten): undviks helt
        const endWall = `${d}T${minToHhmm(m + dur)}`;
        const er = resolveLocal(endWall, g.tz, 'compatible');
        if (er.adjusted || er.zdt.epochMilliseconds !== startMs + dur * 60_000) continue; // passet korsar en omställning
      }
      const endMs = startMs + dur * 60_000;
      if (startMs < g.lowerMs || endMs > g.deadlineMs) continue;
      if (dayBusy.some((b) => b.start < endMs && b.end > startMs)) continue;
      out.push({ startMs, endMs, start: `${d}T${minToHhmm(m)}`, end: `${d}T${minToHhmm(m + dur)}`, date: d });
    }
  }
  g.cache.set(key, out);
  return out;
}

function daysDesc(from: ISODate, to: ISODate): ISODate[] {
  const out: ISODate[] = [];
  for (let d = to; compareDates(d, from) >= 0; d = addDays(d, -1)) out.push(d);
  return out;
}

/** Senaste lediga pass med start ≥ fromMs och slut ≤ toMs. */
function latestSlot(g: Grid, dur: number, fromMs: number, toMs: number): Slot | null {
  if (toMs - fromMs < dur * 60_000) return null;
  const fromD = compareDates(dateOfMs(fromMs, g.tz), g.lowerDate) > 0 ? dateOfMs(fromMs, g.tz) : g.lowerDate;
  for (const d of daysDesc(fromD, dateOfMs(toMs - 1, g.tz))) {
    const s = daySlots(g, d, dur);
    for (let i = s.length - 1; i >= 0; i--) if (s[i]!.startMs >= fromMs && s[i]!.endMs <= toMs) return s[i]!;
  }
  return null;
}

/** Tidigaste lediga pass på dagen d med start ≥ fromMs och slut ≤ toMs. */
function earliestOnDay(g: Grid, d: ISODate, dur: number, fromMs: number, toMs: number): Slot | null {
  for (const s of daySlots(g, d, dur)) if (s.startMs >= fromMs && s.endMs <= toMs) return s;
  return null;
}

function earliestSlot(g: Grid, dur: number, fromMs: number, toMs: number): Slot | null {
  if (toMs - fromMs < dur * 60_000) return null;
  const fromD = compareDates(dateOfMs(fromMs, g.tz), g.lowerDate) > 0 ? dateOfMs(fromMs, g.tz) : g.lowerDate;
  for (let d = fromD; compareDates(d, dateOfMs(toMs - 1, g.tz)) <= 0; d = addDays(d, 1)) {
    const s = earliestOnDay(g, d, dur, fromMs, toMs);
    if (s) return s;
  }
  return null;
}

/** Önskad dag: N kalenderdagar eller N vardagar (mån–fre) före deadlinens datum. */
export function preferredPrepDate(deadlineDate: ISODate, daysBefore: number, dayCount: 'weekdays' | 'calendar'): ISODate {
  if (dayCount === 'calendar') return addDays(deadlineDate, -daysBefore);
  let d = deadlineDate;
  let n = 0;
  for (let guard = 0; n < daysBefore && guard < 100; guard++) {
    d = addDays(d, -1);
    if (dow(d) <= 5) n++;
  }
  return d;
}

interface StepNeed {
  index: number;
  stepId: string;
  title: string;
  dur: number;
  preferred: ISODate;
}

type RunResult = { ok: true; slots: Slot[] } | { ok: false; fits: Array<Slot | null>; noRoom: boolean[] };

/**
 * Placerar en följd av steg i ordning mellan lower och upper.
 *  1. Bakåt: senaste möjliga start för varje steg givet att alla senare steg ryms (exakt genomförbarhet).
 *  2. Framåt: varje steg på önskad dag om möjligt, annars tidigare dagar, sist senare dagar – men aldrig
 *     senare än att resten av planen fortfarande ryms. Därför avvisas aldrig en enkel genomförbar plan av
 *     ett girigt tidigt val (se testet "inte onödigt girig").
 */
function placeRun(g: Grid, steps: StepNeed[], lowerMs: number, upperMs: number): RunResult {
  const latest: number[] = new Array(steps.length + 1);
  latest[steps.length] = upperMs;
  for (let k = steps.length - 1; k >= 0; k--) {
    const s = latestSlot(g, steps[k]!.dur, lowerMs, latest[k + 1]!);
    if (!s) {
      // Vilka steg saknar utrymme? Tidigaste placering i ordning visar var det tar stopp.
      const fits: Array<Slot | null> = [];
      let prev = lowerMs;
      let blocked = false;
      for (const st of steps) {
        const e = blocked ? null : earliestSlot(g, st.dur, prev, upperMs);
        fits.push(e);
        if (e) prev = e.endMs;
        else blocked = true;
      }
      const noRoom = steps.map((st) => !earliestSlot(g, st.dur, lowerMs, upperMs));
      return { ok: false, fits, noRoom };
    }
    latest[k] = s.startMs;
  }
  const slots: Slot[] = [];
  let prev = lowerMs;
  for (let k = 0; k < steps.length; k++) {
    const st = steps[k]!;
    const upper = latest[k + 1]!;
    const prevDay = dateOfMs(prev, g.tz);
    const lastDay = dateOfMs(upper - 1, g.tz);
    let pick: Slot | null = null;
    // Önskad dag, sedan tidigare dagar ned till föregående stegs dag.
    for (let d = st.preferred; !pick && compareDates(d, prevDay) >= 0 && compareDates(d, g.lowerDate) >= 0; d = addDays(d, -1)) {
      if (compareDates(d, lastDay) > 0) continue;
      pick = earliestOnDay(g, d, st.dur, prev, upper);
    }
    // Sista utväg: senare än önskat (men före deadline och utan att tränga ut senare steg).
    for (let d = addDays(st.preferred, 1); !pick && compareDates(d, lastDay) <= 0; d = addDays(d, 1)) {
      if (compareDates(d, prevDay) < 0) continue;
      pick = earliestOnDay(g, d, st.dur, prev, upper);
    }
    // Bakåtpassets pass finns alltid (start ≥ prev, slut ≤ upper) – fallback för säkerhets skull.
    pick ??= latestSlot(g, st.dur, prev, upper);
    if (!pick) return { ok: false, fits: steps.map(() => null), noRoom: steps.map(() => false) };
    slots.push(pick);
    prev = pick.endMs;
  }
  return { ok: true, slots };
}

// ---------------------------------------------------------------------------
// Förslag
// ---------------------------------------------------------------------------

export type PlanSessionAction = 'create' | 'move' | 'keep' | 'done' | 'manual' | 'removed' | 'past' | 'impossible';

export interface PlanSessionProposal {
  stepId: string;
  stepIndex: number;
  title: string;
  durationMinutes: number;
  action: PlanSessionAction;
  /** Tider (för create/move: föreslagen ny tid; annars passets nuvarande tid). Saknas för removed/impossible. */
  start?: PlainDateTimeStr;
  end?: PlainDateTimeStr;
  timeZone?: string;
  date?: ISODate;
  startMs?: number;
  endMs?: number;
  preferredDate: ISODate;
  /** Kalenderdagar från önskad dag: negativt = tidigare, positivt = senare, 0 = önskad dag. */
  deviationDays?: number;
  eventId?: string;
  expectedEventRevision?: number;
  /** Omplanering: passets tid före ändringen. */
  from?: { start: PlainDateTimeStr; end: PlainDateTimeStr; timeZone: string };
  /** Varför passet behöver ändras (omplanering). */
  problem?: { kind: 'conflict'; with: string[] } | { kind: 'after-deadline' } | { kind: 'outside-window' } | { kind: 'order' } | { kind: 'realign' };
  /** Impossible: 'no-room' = ingen ledig tid alls; 'after-previous' = ryms inte efter tidigare steg. */
  why?: 'no-room' | 'after-previous';
  /** Närmast före/efter samma dag (förklaring). */
  before?: { title: string; end: PlainDateTimeStr } | null;
  after?: { title: string; start: PlainDateTimeStr } | null;
}

export type PlanUnsupportedReason = SourceBlock | 'template-missing' | 'template-invalid' | 'plan-active' | 'syncing';

export interface PlanProposal {
  status: 'ok' | 'impossible' | 'unchanged' | 'unsupported';
  reason?: PlanUnsupportedReason;
  kind: 'approve' | 'replan';
  planId: string;
  sourceKey: string;
  sourceTitle: string;
  sourceName: string | null;
  /** När källans senaste import gjordes (hur gammalt underlaget är). */
  sourceImportedAt: ISOInstant | null;
  templateId: string | null;
  templateRevision: number | null;
  snapshot: (PrepTemplateContent & { templateRevision?: number }) | null;
  basis: PrepPlanBasis | null;
  /** Omplanering: deadlinen planen byggdes för (om den skiljer sig). */
  previousBasis?: PrepPlanBasis | null;
  mode?: 'realign' | 'repair';
  /** Omplanering: manuellt ändrade steg som användaren uttryckligen tog med (följer med till godkännandet). */
  include?: string[];
  /** Omplanering: borttagna steg som användaren uttryckligen återställer. */
  restore?: string[];
  /** Tidigaste tillåtna start: "nu" avrundat uppåt till kvart (samma värde hela kvarten → stabila förslag). */
  nowMs: number;
  sessions: PlanSessionProposal[];
  totalMinutes: number;
  createCount: number;
  moveCount: number;
  searchedFrom: ISODate | null;
  searchedTo: ISODate | null;
  uncertain: Array<{ title: string; reason: 'unsupported-recurrence' }>;
  hiddenCalendarsCounted: number;
  existing: ExistingPrep[];
  expectedPlanRevision: number | null;
  /** Kalendern (målkalendern) är dold – passen syns inte i kalendervyn förrän den visas. */
  calendarHidden: boolean;
}

export interface ProposeInput {
  sourceKey: string;
  now: Temporal.Instant;
  /** Ny plan: vald mall. */
  templateId?: string;
  /** Omplanering: manuellt ändrade steg som användaren uttryckligen vill ta med. */
  include?: readonly string[];
  /** Omplanering: borttagna steg som användaren uttryckligen vill återställa. */
  restore?: readonly string[];
  /** Förberäknat underlag (automatiska utkast delar ett fönster). Får inte innehålla planens egna pass. */
  busy?: BusyResult & { from: ISODate; toEx: ISODate };
}

function emptyProposal(state: AppState, input: ProposeInput, kind: 'approve' | 'replan', reason: PlanUnsupportedReason): PlanProposal {
  const src = state.sourceItems[input.sourceKey];
  return {
    status: 'unsupported',
    reason,
    kind,
    planId: prepPlanIdFor(input.sourceKey),
    sourceKey: input.sourceKey,
    sourceTitle: src ? src.data.title || src.data.summary : '',
    sourceName: src ? state.sources[src.sourceId]?.name ?? null : null,
    sourceImportedAt: null,
    templateId: input.templateId ?? null,
    templateRevision: null,
    snapshot: null,
    basis: null,
    nowMs: ceil15(input.now.epochMilliseconds),
    sessions: [],
    totalMinutes: 0,
    createCount: 0,
    moveCount: 0,
    searchedFrom: null,
    searchedTo: null,
    uncertain: [],
    hiddenCalendarsCounted: 0,
    existing: [],
    expectedPlanRevision: null,
    calendarHidden: false,
  };
}

function sourceMeta(state: AppState, src: SourceItem): { name: string | null; importedAt: ISOInstant | null } {
  const s = state.sources[src.sourceId];
  const imports = (s?.imports ?? []).filter((r) => !r.undone);
  const rec = imports.find((r) => r.id === src.lastSeenImportId) ?? imports.at(-1);
  return { name: s?.name ?? null, importedAt: rec?.at ?? null };
}

function makeGrid(state: AppState, content: PrepTemplateContent, basisMs: number, deadlineDate: ISODate, nowMs: number, busy: Busy[]): Grid {
  const tz = state.settings.timeZone;
  const today = dateOfMs(nowMs, tz);
  const lowerDate = compareDates(addDays(deadlineDate, -PREP_HORIZON_DAYS), today) > 0 ? addDays(deadlineDate, -PREP_HORIZON_DAYS) : today;
  const g: Grid = {
    tz,
    window: { start: toMin(content.window.start), end: toMin(content.window.end) },
    weekdays: new Set(content.weekdays),
    lowerMs: 0,
    lowerDate,
    deadlineMs: basisMs,
    deadlineDate,
    busy,
    cache: new Map(),
    dayInfo: new Map(),
  };
  g.lowerMs = Math.max(ceil15(nowMs), dayStart(g, lowerDate).startMs);
  return g;
}

function neighbours(g: Grid, s: Slot): { before: PlanSessionProposal['before']; after: PlanSessionProposal['after'] } {
  const info = dayStart(g, s.date);
  const dayEnd = info.startMs + 26 * 3_600_000;
  const same = g.busy.filter((b) => !b.allDay && b.start < dayEnd && b.end > info.startMs);
  const before = same.filter((b) => b.end <= s.startMs).sort((a, b) => b.end - a.end)[0];
  const after = same.filter((b) => b.start >= s.endMs).sort((a, b) => a.start - b.start)[0];
  return {
    before: before ? { title: before.title, end: wallOf(before.end, g.tz) } : null,
    after: after ? { title: after.title, start: wallOf(after.start, g.tz) } : null,
  };
}

function busyFor(state: AppState, input: ProposeInput, from: ISODate, toEx: ISODate, exclude: ReadonlySet<string>): BusyResult {
  if (input.busy && !exclude.size && compareDates(input.busy.from, from) <= 0 && compareDates(input.busy.toEx, toEx) >= 0) return input.busy;
  return busyIntervals(state, from, toEx, input.sourceKey, exclude);
}

function slotSession(g: Grid, need: StepNeed, s: Slot, action: PlanSessionAction): PlanSessionProposal {
  return {
    stepId: need.stepId,
    stepIndex: need.index,
    title: need.title,
    durationMinutes: need.dur,
    action,
    start: s.start,
    end: s.end,
    timeZone: g.tz,
    date: s.date,
    startMs: s.startMs,
    endMs: s.endMs,
    preferredDate: need.preferred,
    deviationDays: daysBetween(need.preferred, s.date),
    ...neighbours(g, s),
  };
}

/**
 * Föreslår en plan för källdeadlinen. Finns en aktiv plan blir det en omplanering (från planens sparade
 * ögonblicksbild); annars en ny plan från vald mall. Skapar ingenting.
 */
export function proposePlan(state: AppState, input: ProposeInput): PlanProposal {
  const nowMs = input.now.epochMilliseconds;
  const src = state.sourceItems[input.sourceKey];
  const plan = planForSource(state, input.sourceKey);
  const active = plan?.status === 'active' ? plan : undefined;
  if (active) return proposeReplan(state, input, active);
  const block = sourceBlock(state, src, nowMs);
  if (block) return emptyProposal(state, input, 'approve', block);
  const tpl = input.templateId ? state.prepTemplates?.[input.templateId] : undefined;
  if (!tpl) return emptyProposal(state, input, 'approve', 'template-missing');
  if (validatePrepTemplateContent(state, tpl)) return { ...emptyProposal(state, input, 'approve', 'template-invalid'), templateId: tpl.id, templateRevision: tpl.revision, snapshot: { ...contentOf(tpl), templateRevision: tpl.revision } };
  const s = src!;
  const basis = currentBasis(state, s);
  const basisMs = Temporal.Instant.from(basis.deadlineStart).epochMilliseconds;
  const tz = state.settings.timeZone;
  const today = dateOfMs(nowMs, tz);
  const from = compareDates(addDays(basis.deadlineDate, -PREP_HORIZON_DAYS), today) > 0 ? addDays(basis.deadlineDate, -PREP_HORIZON_DAYS) : today;
  const toEx = addDays(basis.deadlineDate, 1);
  const busy = busyFor(state, input, from, toEx, new Set());
  const g = makeGrid(state, tpl, basisMs, basis.deadlineDate, nowMs, busy.busy);
  const needs: StepNeed[] = tpl.steps.map((st, i) => ({ index: i, stepId: st.id, title: st.title, dur: st.durationMinutes, preferred: preferredPrepDate(basis.deadlineDate, st.daysBefore, tpl.dayCount) }));
  const run = placeRun(g, needs, g.lowerMs, basisMs);
  const meta = sourceMeta(state, s);
  const sessions: PlanSessionProposal[] = run.ok
    ? needs.map((n, i) => slotSession(g, n, run.slots[i]!, 'create'))
    : needs.map((n, i) => {
        const f = run.fits[i];
        if (f) return slotSession(g, n, f, 'create');
        return { stepId: n.stepId, stepIndex: n.index, title: n.title, durationMinutes: n.dur, action: 'impossible' as const, preferredDate: n.preferred, why: run.noRoom[i] ? ('no-room' as const) : ('after-previous' as const) };
      });
  return {
    status: run.ok ? 'ok' : 'impossible',
    kind: 'approve',
    planId: prepPlanIdFor(s.key),
    sourceKey: s.key,
    sourceTitle: s.data.title || s.data.summary,
    sourceName: meta.name,
    sourceImportedAt: meta.importedAt,
    templateId: tpl.id,
    templateRevision: tpl.revision,
    snapshot: { ...contentOf(tpl), templateRevision: tpl.revision },
    basis,
    nowMs: ceil15(nowMs),
    sessions,
    totalMinutes: needs.reduce((n, x) => n + x.dur, 0),
    createCount: run.ok ? needs.length : 0,
    moveCount: 0,
    searchedFrom: g.lowerDate,
    searchedTo: basis.deadlineDate,
    uncertain: busy.uncertain,
    hiddenCalendarsCounted: busy.hiddenCalendarsCounted,
    existing: existingPrepOutsidePlan(state, s.key),
    expectedPlanRevision: plan ? plan.revision : null,
    calendarHidden: state.calendars[tpl.calendarId]?.visible === false,
  };
}

function contentOf(t: PrepTemplateContent): PrepTemplateContent {
  return { name: t.name, steps: t.steps.map((s) => ({ ...s })), dayCount: t.dayCount, window: { ...t.window }, weekdays: [...t.weekdays], calendarId: t.calendarId };
}

type StepState = { step: PrepPlanStep; index: number; event: LocalEvent | undefined; state: 'missing-local' | 'removed' | 'done' | 'past' | 'manual' | 'movable' };

function classifySteps(state: AppState, plan: PrepPlan, nowMs: number, include: ReadonlySet<string>): StepState[] {
  return plan.steps.map((step, index) => {
    const event = state.events[step.eventId];
    if (!event) return { step, index, event, state: 'missing-local' as const };
    if (event.deleted) return { step, index, event, state: 'removed' as const };
    if (event.done) return { step, index, event, state: 'done' as const };
    const { start, end } = timingInstants(event.timing, state.settings.timeZone);
    if (end.epochMilliseconds <= nowMs || start.epochMilliseconds <= nowMs) return { step, index, event, state: 'past' as const };
    if ((event.revision !== step.approved.eventRevision || event.recurrence) && !include.has(step.stepId)) return { step, index, event, state: 'manual' as const };
    if (event.recurrence || event.timing.kind !== 'timed') return { step, index, event, state: 'manual' as const };
    return { step, index, event, state: 'movable' as const };
  });
}

function eventSlot(e: LocalEvent, tz: string): Slot {
  const { start, end } = timingInstants(e.timing, tz);
  return {
    startMs: start.epochMilliseconds,
    endMs: end.epochMilliseconds,
    start: e.timing.kind === 'timed' ? e.timing.start : `${e.timing.start}T00:00`,
    end: e.timing.kind === 'timed' ? e.timing.end : `${e.timing.endExclusive}T00:00`,
    date: dateOfMs(start.epochMilliseconds, tz),
  };
}

function basisChanged(a: PrepPlanBasis, b: PrepPlanBasis): boolean {
  return a.deadlineStart !== b.deadlineStart || a.deadlineDate !== b.deadlineDate || a.allDay !== b.allDay || a.timeZone !== b.timeZone;
}

/** Problem för ett befintligt, ändringsbart pass: krock, efter deadline eller utanför mallens ramar. */
function slotProblem(g: Grid, s: Slot, durMin: number): PlanSessionProposal['problem'] | null {
  if (s.endMs > g.deadlineMs) return { kind: 'after-deadline' };
  const clash = g.busy.filter((b) => b.start < s.endMs && b.end > s.startMs);
  if (clash.length) return { kind: 'conflict', with: [...new Set(clash.map((b) => b.title))] };
  const startMin = toMin(s.start.slice(11, 16));
  if (!g.weekdays.has(dow(s.date)) || startMin < g.window.start || startMin + durMin > g.window.end || s.endMs - s.startMs !== durMin * 60_000) return { kind: 'outside-window' };
  return null;
}

function proposeReplan(state: AppState, input: ProposeInput, plan: PrepPlan): PlanProposal {
  const nowMs = input.now.epochMilliseconds;
  const src = state.sourceItems[input.sourceKey];
  const block = sourceBlock(state, src, nowMs);
  if (block) return { ...emptyProposal(state, input, 'replan', block), expectedPlanRevision: plan.revision, templateId: plan.templateId, snapshot: plan.snapshot, previousBasis: plan.basis };
  const s = src!;
  const tz = state.settings.timeZone;
  const include = new Set(input.include ?? []);
  const restore = new Set(input.restore ?? []);
  const classes = classifySteps(state, plan, nowMs, include);
  if (classes.some((c) => c.state === 'missing-local')) return { ...emptyProposal(state, input, 'replan', 'syncing'), expectedPlanRevision: plan.revision, templateId: plan.templateId, snapshot: plan.snapshot };
  if (!state.calendars[plan.snapshot.calendarId] || state.calendars[plan.snapshot.calendarId]!.kind === 'import') {
    return { ...emptyProposal(state, input, 'replan', 'template-invalid'), expectedPlanRevision: plan.revision, templateId: plan.templateId, snapshot: plan.snapshot };
  }
  const basis = currentBasis(state, s);
  const basisMs = Temporal.Instant.from(basis.deadlineStart).epochMilliseconds;
  const realign = basisChanged(plan.basis, basis);
  const movableIds = new Set(classes.filter((c) => c.state === 'movable').map((c) => c.step.eventId));
  const today = dateOfMs(nowMs, tz);
  const from = compareDates(addDays(basis.deadlineDate, -PREP_HORIZON_DAYS), today) > 0 ? addDays(basis.deadlineDate, -PREP_HORIZON_DAYS) : today;
  const toEx = addDays(basis.deadlineDate, 1);
  // Endast planens EGNA, ändringsbara pass undantas från sin egen krockkontroll.
  const busy = busyIntervals(state, from, toEx, s.key, movableIds);
  const g = makeGrid(state, plan.snapshot, basisMs, basis.deadlineDate, nowMs, busy.busy);
  const snapSteps = new Map(plan.snapshot.steps.map((st) => [st.id, st]));
  const needOf = (c: StepState): StepNeed => ({
    index: c.index,
    stepId: c.step.stepId,
    title: c.step.title,
    dur: c.step.durationMinutes,
    preferred: preferredPrepDate(basis.deadlineDate, snapSteps.get(c.step.stepId)?.daysBefore ?? 1, plan.snapshot.dayCount),
  });

  // Vad ska placeras om? Realign (deadline/zon ändrad): alla ändringsbara. Annars bara de med problem.
  const sessions: Array<PlanSessionProposal | null> = classes.map(() => null);
  const toPlace = new Set<number>();
  for (const c of classes) {
    const need = needOf(c);
    if (c.state === 'removed') {
      if (restore.has(c.step.stepId)) toPlace.add(c.index);
      else sessions[c.index] = { stepId: c.step.stepId, stepIndex: c.index, title: c.step.title, durationMinutes: c.step.durationMinutes, action: 'removed', preferredDate: need.preferred, eventId: c.step.eventId };
      continue;
    }
    const slot = eventSlot(c.event!, tz);
    const base: PlanSessionProposal = {
      stepId: c.step.stepId,
      stepIndex: c.index,
      title: c.event!.title,
      durationMinutes: c.step.durationMinutes,
      action: c.state === 'done' ? 'done' : c.state === 'past' ? 'past' : c.state === 'manual' ? 'manual' : 'keep',
      start: slot.start,
      end: slot.end,
      timeZone: c.event!.timing.kind === 'timed' ? c.event!.timing.timeZone : tz,
      date: slot.date,
      startMs: slot.startMs,
      endMs: slot.endMs,
      preferredDate: need.preferred,
      deviationDays: daysBetween(need.preferred, slot.date),
      eventId: c.step.eventId,
      expectedEventRevision: c.event!.revision,
    };
    if (c.state === 'manual' && slot.endMs > basisMs) base.problem = { kind: 'after-deadline' };
    if (c.state === 'movable') {
      const prob = realign ? { kind: 'realign' as const } : slotProblem(g, slot, c.step.durationMinutes);
      if (prob) {
        base.problem = prob;
        toPlace.add(c.index);
      }
    }
    sessions[c.index] = base;
  }
  // Ordningen mellan kvarliggande pass: ett pass som hamnat före ett tidigare steg måste också flyttas.
  if (!realign) {
    let prevEnd = -Infinity;
    for (const c of classes) {
      const sp = sessions[c.index];
      if (!sp || sp.action === 'removed' || toPlace.has(c.index)) continue;
      if (sp.startMs! < prevEnd && c.state === 'movable') {
        sp.problem = { kind: 'order' };
        toPlace.add(c.index);
        continue;
      }
      prevEnd = Math.max(prevEnd, sp.endMs!);
    }
  }

  // Placera varje sammanhängande följd av steg som ska flyttas/skapas mellan fasta grannar.
  const order = classes.map((c) => c.index);
  let impossible = false;
  let i = 0;
  while (i < order.length) {
    if (!toPlace.has(i)) {
      i++;
      continue;
    }
    let j = i;
    while (j + 1 < order.length && toPlace.has(j + 1)) j++;
    // Ordningen hålls mot ALLA tidigare steg (max slut) och alla senare fasta, kommande steg (min start).
    // Klara/passerade senare steg begränsar inte: de ligger redan bakom oss.
    let lower = g.lowerMs;
    for (let k = 0; k < i; k++) {
      const sp = sessions[k];
      if (sp && sp.action !== 'removed' && sp.action !== 'impossible' && sp.endMs !== undefined) lower = Math.max(lower, sp.endMs);
    }
    let upper = basisMs;
    for (let k = j + 1; k < order.length; k++) {
      const sp = sessions[k];
      if (sp && !toPlace.has(k) && (sp.action === 'keep' || sp.action === 'manual') && sp.startMs !== undefined) upper = Math.min(upper, sp.startMs);
    }
    const runClasses = classes.slice(i, j + 1);
    const needs = runClasses.map(needOf);
    const run = placeRun(g, needs, lower, upper);
    runClasses.forEach((c, n) => {
      const prev = sessions[c.index];
      const isRestore = c.state === 'removed';
      if (run.ok) {
        const sp = slotSession(g, needs[n]!, run.slots[n]!, isRestore ? 'create' : 'move');
        sessions[c.index] = {
          ...sp,
          eventId: c.step.eventId,
          ...(isRestore ? {} : { expectedEventRevision: c.event!.revision, from: { start: prev!.start!, end: prev!.end!, timeZone: prev!.timeZone! }, ...(prev?.problem ? { problem: prev.problem } : {}) }),
        };
        // Samma tid som förut = ligger kvar (t.ex. realign där passet redan ligger på bästa platsen).
        if (!isRestore && prev && prev.start === sp.start && prev.end === sp.end) sessions[c.index] = { ...prev, action: 'keep', problem: undefined } as PlanSessionProposal;
      } else {
        impossible = true;
        sessions[c.index] = {
          stepId: c.step.stepId,
          stepIndex: c.index,
          title: c.step.title,
          durationMinutes: c.step.durationMinutes,
          action: 'impossible',
          preferredDate: needs[n]!.preferred,
          eventId: c.step.eventId,
          why: run.noRoom[n] ? 'no-room' : 'after-previous',
          ...(prev && !isRestore ? { from: { start: prev.start!, end: prev.end!, timeZone: prev.timeZone! }, problem: prev.problem } : {}),
        };
      }
    });
    i = j + 1;
  }
  const list = sessions.map((x) => x!);
  const createCount = list.filter((x) => x.action === 'create').length;
  const moveCount = list.filter((x) => x.action === 'move').length;
  const meta = sourceMeta(state, s);
  return {
    status: impossible ? 'impossible' : createCount + moveCount ? 'ok' : 'unchanged',
    kind: 'replan',
    mode: realign ? 'realign' : 'repair',
    include: [...include],
    restore: [...restore],
    planId: plan.id,
    sourceKey: s.key,
    sourceTitle: s.data.title || s.data.summary,
    sourceName: meta.name,
    sourceImportedAt: meta.importedAt,
    templateId: plan.templateId,
    templateRevision: plan.snapshot.templateRevision,
    snapshot: plan.snapshot,
    basis,
    previousBasis: plan.basis,
    nowMs: ceil15(nowMs),
    sessions: list,
    totalMinutes: list.filter((x) => x.action !== 'removed').reduce((n, x) => n + x.durationMinutes, 0),
    createCount,
    moveCount,
    searchedFrom: g.lowerDate,
    searchedTo: basis.deadlineDate,
    uncertain: busy.uncertain,
    hiddenCalendarsCounted: busy.hiddenCalendarsCounted,
    existing: existingPrepOutsidePlan(state, s.key),
    expectedPlanRevision: plan.revision,
    calendarHidden: state.calendars[plan.snapshot.calendarId]?.visible === false,
  };
}

// ---------------------------------------------------------------------------
// Godkännande: begäran, validering mot AKTUELLT underlag, dokument att skriva
// ---------------------------------------------------------------------------

export interface PlanSessionChoice {
  stepId: string;
  action: 'create' | 'move';
  start: PlainDateTimeStr;
  end: PlainDateTimeStr;
  timeZone: string;
  eventId?: string;
  expectedEventRevision?: number;
}

export interface PlanCommitApprove {
  kind: 'approve';
  opId: string;
  planId: string;
  sourceKey: string;
  templateId: string;
  templateRevision: number;
  basis: PrepPlanBasis;
  expectedPlanRevision: number | null;
  sessions: PlanSessionChoice[];
  /** Anteckning per steg (på användarens språk vid godkännandet; blir sedan användarens text). */
  notes: Record<string, string>;
  origin: 'manual' | 'rule';
  ruleId?: string | null;
  /** Användaren har sett befintliga förberedelser och valt att lägga till planen ändå. */
  withExisting?: boolean;
}

export interface PlanCommitReplan {
  kind: 'replan';
  opId: string;
  planId: string;
  sourceKey: string;
  basis: PrepPlanBasis;
  expectedPlanRevision: number;
  sessions: PlanSessionChoice[];
  include: string[];
  restore: string[];
  notes: Record<string, string>;
}

export interface PlanCommitEnd {
  kind: 'end';
  opId: string;
  planId: string;
  sourceKey: string;
  expectedPlanRevision: number;
  /** true = ta bort planens kommande, ej klara pass som planen själv lagt (manuellt ändrade ligger kvar). */
  removeFuture: boolean;
}

/** Gästöverföring: en BEFINTLIG plan med sina pass, exakt en gång. Kör aldrig planmotorn. */
export interface PlanCommitImport {
  kind: 'import';
  opId: string;
  plan: PrepPlan;
  events: LocalEvent[];
}

export type PlanCommitRequest = PlanCommitApprove | PlanCommitReplan | PlanCommitEnd | PlanCommitImport;

export interface PlanCommitBuilt {
  change: Change;
  planId: string;
  plan: PrepPlan;
  /** Kalenderpass som skapas, flyttas eller tas bort (tombstone). */
  events: LocalEvent[];
  label: string;
  /** Import: planen fanns redan – ingenting skrivs. */
  skipped?: boolean;
}

/** Bygger en godkännandebegäran av ett förslag. Inga tider ändras här. */
export function commitRequestFromProposal(p: PlanProposal, opId: string, notes: Record<string, string>, extra: { origin?: 'manual' | 'rule'; ruleId?: string | null; withExisting?: boolean; include?: string[]; restore?: string[] } = {}): PlanCommitApprove | PlanCommitReplan {
  if (p.status !== 'ok' || !p.basis) throw new ValidationError('Förslaget kan inte godkännas.');
  const sessions: PlanSessionChoice[] = p.sessions
    .filter((s) => s.action === 'create' || s.action === 'move')
    .map((s) => ({
      stepId: s.stepId,
      action: s.action as 'create' | 'move',
      start: s.start!,
      end: s.end!,
      timeZone: s.timeZone!,
      ...(s.eventId ? { eventId: s.eventId } : {}),
      ...(s.expectedEventRevision !== undefined ? { expectedEventRevision: s.expectedEventRevision } : {}),
    }));
  if (p.kind === 'approve') {
    return {
      kind: 'approve',
      opId,
      planId: p.planId,
      sourceKey: p.sourceKey,
      templateId: p.templateId!,
      templateRevision: p.templateRevision!,
      basis: p.basis,
      expectedPlanRevision: p.expectedPlanRevision,
      sessions,
      notes,
      origin: extra.origin ?? 'manual',
      ruleId: extra.ruleId ?? null,
      withExisting: !!extra.withExisting,
    };
  }
  return { kind: 'replan', opId, planId: p.planId, sourceKey: p.sourceKey, basis: p.basis, expectedPlanRevision: p.expectedPlanRevision!, sessions, include: extra.include ?? p.include ?? [], restore: extra.restore ?? p.restore ?? [], notes };
}

function sameBasis(a: PrepPlanBasis, b: PrepPlanBasis): PlanStaleReason | null {
  if (a.timeZone !== b.timeZone) return 'zone-changed';
  if (a.deadlineStart !== b.deadlineStart || a.deadlineDate !== b.deadlineDate || a.allDay !== b.allDay) return 'deadline-changed';
  if (a.sourceHash !== b.sourceHash) return 'source-changed';
  return null;
}

const NOTE_MAX = 2000;

function sessionEvent(state: AppState, plan: { sourceKey: string; snapshot: Pick<PrepTemplateContent, 'calendarId'> }, title: string, choice: PlanSessionChoice, id: string, notes: string, now: Temporal.Instant): LocalEvent {
  const cal = state.calendars[plan.snapshot.calendarId]!;
  const work = cal.kind === 'work';
  return {
    id,
    calendarId: cal.id,
    title,
    categoryId: work ? 'arbete' : 'privat',
    icon: work ? 'briefcase' : 'home',
    timing: { kind: 'timed', start: choice.start, end: choice.end, timeZone: choice.timeZone, durationPolicy: 'elapsed' },
    ...(notes ? { notes: notes.slice(0, NOTE_MAX) } : {}),
    alarms: [{ id: 'a1', kind: 'relative', offsetMinutes: PREP_ALARM_OFFSET_MINUTES, related: 'start', action: 'DISPLAY', origin: 'user' }],
    linkedSourceKey: plan.sourceKey,
    done: null,
    exportUid: exportUidFor(id),
    revision: 1,
    createdAt: iso(now),
    updatedAt: iso(now),
  };
}

/** Kontrollerar ett valt pass mot aktuellt underlag. Kastar PlanStaleError. */
function checkChoice(g: Grid, c: PlanSessionChoice, dur: number, nowMs: number, lowerDate: ISODate): Slot {
  if (c.timeZone !== g.tz) throw new PlanStaleError('zone-changed');
  if (typeof c.start !== 'string' || typeof c.end !== 'string' || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(c.start) || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(c.end)) throw new PlanStaleError('invalid');
  const d = c.start.slice(0, 10);
  if (c.end.slice(0, 10) !== d) throw new PlanStaleError('invalid');
  const startMin = toMin(c.start.slice(11, 16));
  if (startMin % 15 || toMin(c.end.slice(11, 16)) !== startMin + dur) throw new PlanStaleError('invalid', 'längd');
  if (compareDates(d, lowerDate) < 0) throw new PlanStaleError('time-passed');
  const slots = daySlots(g, d, dur);
  const hit = slots.find((s) => s.start === c.start && s.end === c.end);
  if (hit) return hit;
  // Varför inte? Ge ett ärligt skäl.
  const r = resolveLocal(c.start, g.tz, 'compatible');
  const startMs = r.zdt.epochMilliseconds;
  if (startMs < nowMs) throw new PlanStaleError('time-passed');
  if (startMs + dur * 60_000 > g.deadlineMs) throw new PlanStaleError('deadline-changed');
  if (!g.weekdays.has(dow(d)) || startMin < g.window.start || startMin + dur > g.window.end) throw new PlanStaleError('invalid', 'utanför tillåten tid');
  throw new PlanStaleError('slot-taken');
}

function pushHistory(plan: PrepPlan, entry: PrepPlan['history'][number]): PrepPlan['history'] {
  return [...(plan.history ?? []), entry].slice(-20);
}

/**
 * Validerar en godkännandebegäran mot AKTUELLT tillstånd och bygger alla dokument som ska skrivas
 * tillsammans (plan + kalenderpass). Används av gästprofilen (en lokal transaktion) och av servern (en
 * SQL-transaktion under användarens lås). Kastar PlanStaleError om något i underlaget har ändrats;
 * det gamla förslaget godkänner aldrig nya tider.
 */
export function buildPlanCommit(state: AppState, req: PlanCommitRequest, now: Temporal.Instant): PlanCommitBuilt {
  const nowMs = now.epochMilliseconds;
  if (req.kind === 'import') return buildImport(state, req, now);
  const plan = state.prepPlans?.[req.planId];
  if (req.planId !== prepPlanIdFor(req.sourceKey)) throw new PlanStaleError('invalid');
  if (req.kind === 'end') {
    if (!plan || plan.status !== 'active') throw new PlanStaleError('plan-changed');
    if (plan.revision !== req.expectedPlanRevision) throw new PlanStaleError('plan-changed');
    const events: LocalEvent[] = [];
    if (req.removeFuture) {
      for (const st of plan.steps) {
        const e = state.events[st.eventId];
        if (!e || e.deleted || e.done || e.revision !== st.approved.eventRevision) continue;
        if (timingInstants(e.timing, state.settings.timeZone).start.epochMilliseconds <= nowMs) continue;
        events.push({ ...e, deleted: true, revision: e.revision + 1, updatedAt: iso(now) });
      }
    }
    const next: PrepPlan = { ...plan, status: 'ended', endedAt: iso(now), history: pushHistory(plan, { at: iso(now), kind: 'end', opId: req.opId }), revision: plan.revision + 1, updatedAt: iso(now) };
    return { change: { prepPlans: { [plan.id]: next }, ...(events.length ? { events: Object.fromEntries(events.map((e) => [e.id, e])) } : {}) }, planId: plan.id, plan: next, events, label: 'Planen avslutad' };
  }

  const src = state.sourceItems[req.sourceKey];
  const block = sourceBlock(state, src, nowMs);
  if (block) throw new PlanStaleError(BLOCK_TO_STALE[block]);
  const s = src!;
  const tz = state.settings.timeZone;
  const basis = currentBasis(state, s);
  const diff = sameBasis(req.basis, basis);
  if (diff) throw new PlanStaleError(diff);
  const basisMs = Temporal.Instant.from(basis.deadlineStart).epochMilliseconds;
  if (!Array.isArray(req.sessions) || req.sessions.length > PREP_MAX_STEPS) throw new PlanStaleError('invalid');
  const notes = (id: string) => (typeof req.notes?.[id] === 'string' ? req.notes[id]!.slice(0, NOTE_MAX) : '');
  const today = dateOfMs(nowMs, tz);
  const lowerDate = compareDates(addDays(basis.deadlineDate, -PREP_HORIZON_DAYS), today) > 0 ? addDays(basis.deadlineDate, -PREP_HORIZON_DAYS) : today;

  if (req.kind === 'approve') {
    if (plan?.status === 'active') throw new PlanStaleError('plan-exists');
    if ((plan?.revision ?? null) !== req.expectedPlanRevision) throw new PlanStaleError('plan-changed');
    const tpl = state.prepTemplates?.[req.templateId];
    if (!tpl) throw new PlanStaleError('template-deleted');
    if (tpl.revision !== req.templateRevision) throw new PlanStaleError('template-changed');
    const issue = validatePrepTemplateContent(state, tpl);
    if (issue) throw new PlanStaleError(issue.code === 'calendar' ? 'calendar-invalid' : 'invalid');
    if (existingPrepOutsidePlan(state, s.key).length && !req.withExisting) throw new PlanStaleError('singles-exist');
    if (req.sessions.length !== tpl.steps.length) throw new PlanStaleError('invalid');
    const g = makeGrid(state, tpl, basisMs, basis.deadlineDate, nowMs, busyIntervals(state, lowerDate, addDays(basis.deadlineDate, 1), s.key).busy);
    const generation = (plan?.generation ?? 0) + 1;
    const events: LocalEvent[] = [];
    const steps: PrepPlanStep[] = [];
    let prevEnd = -Infinity;
    tpl.steps.forEach((st, i) => {
      const c = req.sessions[i]!;
      if (c.stepId !== st.id || c.action !== 'create') throw new PlanStaleError('invalid');
      const slot = checkChoice(g, c, st.durationMinutes, nowMs, lowerDate);
      if (slot.startMs < prevEnd) throw new PlanStaleError('slot-taken', 'ordning');
      prevEnd = slot.endMs;
      const id = prepEventIdFor(req.planId, generation, st.id, 1);
      if (state.events[id]) throw new PlanStaleError('plan-changed');
      const ev = sessionEvent(state, { sourceKey: s.key, snapshot: tpl }, st.title, c, id, notes(st.id), now);
      events.push(ev);
      steps.push({ stepId: st.id, title: st.title, durationMinutes: st.durationMinutes, eventId: id, approved: { start: c.start, end: c.end, timeZone: c.timeZone, eventRevision: ev.revision, at: iso(now) }, instance: 1 });
    });
    const next: PrepPlan = {
      id: req.planId,
      sourceKey: s.key,
      status: 'active',
      generation,
      origin: req.origin === 'rule' ? 'rule' : 'manual',
      ruleId: req.origin === 'rule' ? req.ruleId ?? null : null,
      templateId: tpl.id,
      snapshot: { ...contentOf(tpl), templateRevision: tpl.revision },
      basis,
      steps,
      acknowledged: [],
      history: [...(plan?.history ?? []), { at: iso(now), kind: 'approve' as const, opId: req.opId }].slice(-20),
      endedAt: null,
      revision: (plan?.revision ?? 0) + 1,
      createdAt: plan?.createdAt ?? iso(now),
      updatedAt: iso(now),
    };
    return { change: { prepPlans: { [next.id]: next }, events: Object.fromEntries(events.map((e) => [e.id, e])) }, planId: next.id, plan: next, events, label: `Förberedelseplan för "${s.data.title || s.data.summary}"` };
  }

  // Omplanering: utgår från planens SPARADE ögonblicksbild (senare mallredigering blockerar inte).
  if (!plan || plan.status !== 'active') throw new PlanStaleError('plan-changed');
  if (plan.revision !== req.expectedPlanRevision) throw new PlanStaleError('plan-changed');
  const cal = state.calendars[plan.snapshot.calendarId];
  if (!cal || cal.kind === 'import') throw new PlanStaleError('calendar-invalid');
  const include = new Set(req.include ?? []);
  const restore = new Set(req.restore ?? []);
  const classes = classifySteps(state, plan, nowMs, include);
  if (classes.some((c) => c.state === 'missing-local')) throw new PlanStaleError('plan-changed', 'ofullständig data');
  const byStep = new Map(classes.map((c) => [c.step.stepId, c]));
  const moving = new Set<string>();
  for (const c of req.sessions) {
    const cl = byStep.get(c.stepId);
    if (!cl) throw new PlanStaleError('invalid');
    if (c.action === 'move') {
      if (cl.state !== 'movable' || c.eventId !== cl.step.eventId || cl.event!.revision !== c.expectedEventRevision) throw new PlanStaleError('event-changed');
      moving.add(cl.step.eventId);
    } else if (c.action === 'create') {
      if (cl.state !== 'removed' || !restore.has(c.stepId)) throw new PlanStaleError('event-changed');
    } else throw new PlanStaleError('invalid');
  }
  if (!req.sessions.length) throw new PlanStaleError('invalid');
  const g = makeGrid(state, plan.snapshot, basisMs, basis.deadlineDate, nowMs, busyIntervals(state, lowerDate, addDays(basis.deadlineDate, 1), s.key, moving).busy);
  const chosen = new Map(req.sessions.map((c) => [c.stepId, c]));
  // Slutliga positioner: valda pass på sin nya tid, fasta pass där de ligger. Samma ordningsregel som
  // förslaget: ett valt pass ligger efter alla tidigare steg och före alla senare fasta, kommande steg.
  const finalSlots: Array<Slot | null> = classes.map((cl) => {
    const c = chosen.get(cl.step.stepId);
    if (c) return checkChoice(g, c, cl.step.durationMinutes, nowMs, lowerDate);
    return cl.state !== 'removed' && cl.event ? eventSlot(cl.event, tz) : null;
  });
  for (const cl of classes) {
    if (!chosen.has(cl.step.stepId)) continue;
    const mine = finalSlots[cl.index]!;
    for (const other of classes) {
      const o = finalSlots[other.index];
      if (!o || other.index === cl.index) continue;
      if (other.index < cl.index && o.endMs > mine.startMs) throw new PlanStaleError('slot-taken', 'ordning');
      const otherFixedUpcoming = !chosen.has(other.step.stepId) && (other.state === 'movable' || other.state === 'manual');
      if (other.index > cl.index && otherFixedUpcoming && mine.endMs > o.startMs) throw new PlanStaleError('slot-taken', 'ordning');
    }
  }
  const events: LocalEvent[] = [];
  const steps: PrepPlanStep[] = [];
  for (const cl of classes) {
    const c = chosen.get(cl.step.stepId);
    if (!c) {
      steps.push(cl.step);
      continue;
    }
    if (c.action === 'move') {
      const e = cl.event!;
      const moved: LocalEvent = { ...e, timing: { kind: 'timed', start: c.start, end: c.end, timeZone: c.timeZone, durationPolicy: e.timing.kind === 'timed' ? e.timing.durationPolicy : 'elapsed' }, revision: e.revision + 1, updatedAt: iso(now) };
      events.push(moved);
      steps.push({ ...cl.step, approved: { start: c.start, end: c.end, timeZone: c.timeZone, eventRevision: moved.revision, at: iso(now) } });
    } else {
      const instance = cl.step.instance + 1;
      const id = prepEventIdFor(plan.id, plan.generation, cl.step.stepId, instance);
      if (state.events[id]) throw new PlanStaleError('plan-changed');
      const ev = sessionEvent(state, plan, cl.step.title, c, id, notes(cl.step.stepId), now);
      events.push(ev);
      steps.push({ ...cl.step, eventId: id, instance, approved: { start: c.start, end: c.end, timeZone: c.timeZone, eventRevision: ev.revision, at: iso(now) } });
    }
  }
  const next: PrepPlan = {
    ...plan,
    basis,
    steps,
    acknowledged: [],
    history: pushHistory(plan, { at: iso(now), kind: restore.size && !moving.size ? 'restore' : 'replan', opId: req.opId }),
    revision: plan.revision + 1,
    updatedAt: iso(now),
  };
  return { change: { prepPlans: { [next.id]: next }, events: Object.fromEntries(events.map((e) => [e.id, e])) }, planId: next.id, plan: next, events, label: `Ändrad förberedelseplan för "${s.data.title || s.data.summary}"` };
}

function buildImport(state: AppState, req: PlanCommitImport, now: Temporal.Instant): PlanCommitBuilt {
  const plan = req.plan;
  const shape = validatePrepDoc('prepPlan', plan?.id, plan);
  if (shape) throw new PlanStaleError('invalid', shape);
  if (plan.id !== prepPlanIdFor(plan.sourceKey)) throw new PlanStaleError('invalid');
  if (!Array.isArray(req.events) || req.events.length > PREP_MAX_STEPS) throw new PlanStaleError('invalid');
  const existing = state.prepPlans?.[plan.id];
  if (existing) return { change: {}, planId: plan.id, plan: existing, events: [], label: 'Planen fanns redan', skipped: true };
  const owned = new Set(plan.steps.map((s) => s.eventId));
  const write: LocalEvent[] = [];
  for (const e of req.events) {
    if (!e || typeof e !== 'object' || !owned.has(e.id) || e.linkedSourceKey !== plan.sourceKey) throw new PlanStaleError('invalid');
    const cur = state.events[e.id];
    if (cur && canonicalJson(cur) !== canonicalJson(e)) return { change: {}, planId: plan.id, plan, events: [], label: 'Passen fanns redan', skipped: true };
    if (!cur) write.push(e);
  }
  const next: PrepPlan = { ...plan, history: pushHistory(plan, { at: iso(now), kind: 'import', opId: req.opId }) };
  return { change: { prepPlans: { [next.id]: next }, ...(write.length ? { events: Object.fromEntries(write.map((e) => [e.id, e])) } : {}) }, planId: next.id, plan: next, events: write, label: 'Förberedelseplan överförd' };
}

/** Gästprofilen: hela godkännandet i EN lokal transaktion (AppStore.run). Samma opId två gånger = inget nytt. */
export function applyPlanCommit(state: AppState, req: PlanCommitRequest, now: Temporal.Instant): OpResult & { planId: string; skipped: boolean } {
  if (state.appliedOps[req.opId]) return { state, change: {}, undo: null, duplicate: true, planId: req.kind === 'import' ? req.plan.id : req.planId, skipped: false };
  const built = buildPlanCommit(state, req, now);
  return { ...commitPrep(state, req.opId, built.label, built.change, now), planId: built.planId, skipped: !!built.skipped };
}

/** Användaren behåller ett pass trots krock: krocken flaggas inte igen (förrän den ändras). */
export function acknowledgePlanConflicts(state: AppState, opId: string, planId: string, fingerprints: string[], now: Temporal.Instant): OpResult {
  const plan = state.prepPlans?.[planId];
  if (!plan) throw new NotFoundError('Planen');
  const next: PrepPlan = { ...plan, acknowledged: [...new Set([...(plan.acknowledged ?? []), ...fingerprints])].slice(-50), history: pushHistory(plan, { at: iso(now), kind: 'acknowledge', opId }), revision: plan.revision + 1, updatedAt: iso(now) };
  return commitPrep(state, opId, 'Krocken behålls', { prepPlans: { [planId]: next } }, now);
}

// ---------------------------------------------------------------------------
// Granskning av en godkänd plan (signaler – flyttar aldrig något själv)
// ---------------------------------------------------------------------------

export type PlanSignal =
  | { kind: 'deadline-moved'; from: { date: ISODate; start: ISOInstant }; to: { date: ISODate; start: ISOInstant } }
  | { kind: 'zone-changed'; from: string; to: string }
  | { kind: 'source-missing' }
  | { kind: 'source-gone' }
  | { kind: 'source-review' }
  | { kind: 'conflict'; stepId: string; eventId: string; with: string[]; fingerprint: string }
  | { kind: 'after-deadline'; stepId: string; eventId: string };

export interface PlanStepStatus {
  stepId: string;
  title: string;
  eventId: string;
  state: 'planned' | 'done' | 'manual' | 'removed' | 'syncing' | 'past';
  event?: LocalEvent;
  date?: ISODate;
}

export interface PlanReview {
  planId: string;
  sourceKey: string;
  status: 'active' | 'ended';
  /** syncing = något pass finns inte lokalt än (ofullständig synk) – visa aldrig "komplett". */
  completeness: 'complete' | 'syncing';
  needsReview: boolean;
  signals: PlanSignal[];
  steps: PlanStepStatus[];
  doneCount: number;
  /** Pass som finns kvar (inte borttagna). */
  activeCount: number;
  total: number;
  /** Alla kvarvarande pass klara. Klarmarkerar ALDRIG deadlinen. */
  allDone: boolean;
  deadlinePassed: boolean;
}

/** Status och granskningssignaler för en plan. Ren funktion – samma underlag i alla vyer. */
export function reviewPlan(state: AppState, planId: string, now: Temporal.Instant): PlanReview | null {
  const plan = state.prepPlans?.[planId];
  if (!plan) return null;
  const nowMs = now.epochMilliseconds;
  const tz = state.settings.timeZone;
  const steps: PlanStepStatus[] = plan.steps.map((st) => {
    const e = state.events[st.eventId];
    if (!e) return { stepId: st.stepId, title: st.title, eventId: st.eventId, state: 'syncing' as const };
    if (e.deleted) return { stepId: st.stepId, title: st.title, eventId: st.eventId, state: 'removed' as const };
    const span = timingInstants(e.timing, tz);
    const date = dateOfMs(span.start.epochMilliseconds, tz);
    if (e.done) return { stepId: st.stepId, title: e.title, eventId: st.eventId, state: 'done' as const, event: e, date };
    if (span.end.epochMilliseconds <= nowMs) return { stepId: st.stepId, title: e.title, eventId: st.eventId, state: 'past' as const, event: e, date };
    if (e.revision !== st.approved.eventRevision) return { stepId: st.stepId, title: e.title, eventId: st.eventId, state: 'manual' as const, event: e, date };
    return { stepId: st.stepId, title: e.title, eventId: st.eventId, state: 'planned' as const, event: e, date };
  });
  const src = state.sourceItems[plan.sourceKey];
  const signals: PlanSignal[] = [];
  let deadlinePassed = false;
  if (plan.status === 'active') {
    if (!src || src.archived) signals.push({ kind: 'source-gone' });
    else {
      const dl = deadlineStart(src, tz);
      deadlinePassed = dl.ms <= nowMs;
      if (src.presence === 'missing') signals.push({ kind: 'source-missing' });
      else if (state.overlays[src.key]?.review) signals.push({ kind: 'source-review' });
      if (!deadlinePassed) {
        const cur = currentBasis(state, src);
        if (cur.deadlineStart !== plan.basis.deadlineStart || cur.deadlineDate !== plan.basis.deadlineDate) {
          signals.push({ kind: 'deadline-moved', from: { date: plan.basis.deadlineDate, start: plan.basis.deadlineStart }, to: { date: cur.deadlineDate, start: cur.deadlineStart } });
        }
        if (cur.timeZone !== plan.basis.timeZone) signals.push({ kind: 'zone-changed', from: plan.basis.timeZone, to: cur.timeZone });
        // Krockar och pass efter deadline – bara för kommande, ej klara pass som planen kontrollerar.
        const upcoming = steps.filter((s) => s.state === 'planned' || s.state === 'manual');
        if (upcoming.length) {
          const today = dateOfMs(nowMs, tz);
          const from = compareDates(addDays(dl.date, -PREP_HORIZON_DAYS), today) > 0 ? addDays(dl.date, -PREP_HORIZON_DAYS) : today;
          const busy = busyIntervals(state, from, addDays(dl.date, 1), src.key).busy;
          const acked = new Set(plan.acknowledged ?? []);
          for (const st of upcoming) {
            const sl = eventSlot(st.event!, tz);
            if (sl.endMs > dl.ms) signals.push({ kind: 'after-deadline', stepId: st.stepId, eventId: st.eventId });
            const clash = busy.filter((b) => b.ref !== st.eventId && b.start < sl.endMs && b.end > sl.startMs);
            if (clash.length) {
              const fp = conflictFingerprint(st.eventId, sl, clash);
              if (!acked.has(fp)) signals.push({ kind: 'conflict', stepId: st.stepId, eventId: st.eventId, with: [...new Set(clash.map((b) => b.title))], fingerprint: fp });
            }
          }
        }
      }
    }
  }
  const remaining = steps.filter((s) => s.state !== 'removed');
  const doneCount = steps.filter((s) => s.state === 'done').length;
  return {
    planId: plan.id,
    sourceKey: plan.sourceKey,
    status: plan.status,
    completeness: steps.some((s) => s.state === 'syncing') ? 'syncing' : 'complete',
    needsReview: plan.status === 'active' && signals.length > 0,
    signals,
    steps,
    doneCount,
    activeCount: remaining.length,
    total: steps.length,
    allDone: remaining.length > 0 && remaining.every((s) => s.state === 'done'),
    deadlinePassed,
  };
}

function conflictFingerprint(eventId: string, s: Slot, clash: Busy[]): string {
  return 'cf_' + sha256Text(`${eventId}|${s.startMs}|${s.endMs}|${clash.map((b) => `${b.ref ?? ''}@${b.start}-${b.end}`).sort().join(',')}`).slice(0, 20);
}

// ---------------------------------------------------------------------------
// Automatiska utkast (opt-in per källa) – räknas bara när appen är öppen
// ---------------------------------------------------------------------------

export type DraftSkipReason =
  | 'passed'
  | 'done'
  | 'archived'
  | 'missing'
  | 'review'
  | 'not-deadline'
  | 'recurring'
  | 'has-plan'
  | 'plan-ended'
  | 'dismissed'
  | 'has-existing-prep'
  | 'template-missing';

export interface AutoDraft {
  sourceKey: string;
  ruleId: string;
  templateId: string;
  proposal: PlanProposal;
}

export interface AutoDraftSkip {
  sourceKey: string;
  ruleId: string;
  title: string;
  date: ISODate;
  reason: DraftSkipReason;
}

export interface AutoDraftRuleSummary {
  ruleId: string;
  sourceId: string;
  sourceName: string;
  templateId: string;
  templateName: string | null;
  status: PrepRule['status'];
  pausedReason: PrepRule['pausedReason'];
  drafts: number;
  skipped: number;
}

export interface AutoDraftResult {
  drafts: AutoDraft[];
  /** Kommande deadlines (inom horisonten) från regelns källa som inte fick något utkast – och varför. */
  skipped: AutoDraftSkip[];
  rules: AutoDraftRuleSummary[];
  /** Fler än MAX_AUTO_DRAFTS kvalificerade – de närmaste visas. */
  truncated: boolean;
  horizonDays: number;
}

/**
 * Utkast enligt användarens uttryckliga regler: kompatibla engångsdeadlines inom kommande 21 dagar från
 * regelns källa. Skapar, flyttar, raderar eller klarmarkerar ALDRIG något. Deterministisk: samma tillstånd
 * och samma kvart ger samma utkast (stabila listor, ingen omräkningsloop – utkasten sparas inte).
 */
export function computeAutoDrafts(state: AppState, now: Temporal.Instant, opts: { max?: number } = {}): AutoDraftResult {
  const nowMs = now.epochMilliseconds;
  const tz = state.settings.timeZone;
  const today = dateOfMs(nowMs, tz);
  const horizonEnd = addDays(today, PREP_HORIZON_DAYS);
  const max = Math.max(1, Math.min(MAX_AUTO_DRAFTS, opts.max ?? MAX_AUTO_DRAFTS));
  const rules = Object.values(state.prepRules ?? {}).sort((a, b) => a.id.localeCompare(b.id));
  const summaries: AutoDraftRuleSummary[] = [];
  const skipped: AutoDraftSkip[] = [];
  const candidates: Array<{ rule: PrepRule; src: SourceItem; ms: number }> = [];
  const index = prepLinkIndex(state);
  for (const rule of rules) {
    const source = state.sources[rule.sourceId];
    const tpl = state.prepTemplates?.[rule.templateId];
    const summary: AutoDraftRuleSummary = {
      ruleId: rule.id,
      sourceId: rule.sourceId,
      sourceName: source?.name ?? rule.sourceId,
      templateId: rule.templateId,
      templateName: tpl?.name ?? null,
      status: rule.status,
      pausedReason: rule.pausedReason ?? null,
      drafts: 0,
      skipped: 0,
    };
    summaries.push(summary);
    if (rule.status !== 'active') continue;
    for (const src of Object.values(state.sourceItems)) {
      if (src.sourceId !== rule.sourceId || src.recurrenceId) continue; // förekomst-override hör till huvudposten
      // Bara deadlines inom horisonten förklaras; poster längre fram eller bakåt är tysta.
      const dl = deadlineStart(src, tz);
      if (compareDates(dl.date, today) < 0 || compareDates(dl.date, horizonEnd) > 0) continue;
      const skip = (reason: DraftSkipReason) => {
        skipped.push({ sourceKey: src.key, ruleId: rule.id, title: src.data.title || src.data.summary, date: dl.date, reason });
        summary.skipped++;
      };
      // Vanliga kalenderposter (t.ex. importerade möten) är inga deadlines och nämns inte. Ett uttryckligt
      // "Inte en deadline" på en post från en deadlinekälla förklaras däremot.
      if (!isDeadline(state, { ref: { kind: 'source', key: src.key } })) {
        if (state.overlays[src.key]?.deadline === false) skip('not-deadline');
        continue;
      }
      const block = sourceBlock(state, src, nowMs);
      if (block) {
        skip(block === 'archived' || block === 'not-found' ? 'archived' : block === 'missing' ? 'missing' : block === 'review' ? 'review' : block === 'not-deadline' ? 'not-deadline' : block === 'recurring' ? 'recurring' : block === 'done' ? 'done' : 'passed');
        continue;
      }
      const plan = planForSource(state, src.key);
      if (plan?.status === 'active') {
        skip('has-plan');
        continue;
      }
      if (plan?.status === 'ended') {
        skip('plan-ended');
        continue;
      }
      if (isPrepDismissed(state, src.key)) {
        skip('dismissed');
        continue;
      }
      if (existingPrepOutsidePlan(state, src.key, index).length) {
        skip('has-existing-prep');
        continue;
      }
      if (!tpl) {
        skip('template-missing');
        continue;
      }
      candidates.push({ rule, src, ms: dl.ms });
    }
  }
  candidates.sort((a, b) => a.ms - b.ms || a.src.key.localeCompare(b.src.key));
  const picked = candidates.slice(0, max);
  // Ett gemensamt underlag för alla utkast (samma definition av upptagen tid, beräknat en gång).
  let shared: (BusyResult & { from: ISODate; toEx: ISODate }) | undefined;
  if (picked.length) {
    const lastDate = deadlineStart(picked.at(-1)!.src, tz).date;
    const from = today;
    const toEx = addDays(compareDates(lastDate, horizonEnd) > 0 ? lastDate : horizonEnd, 1);
    shared = { ...busyIntervals(state, from, toEx), from, toEx };
  }
  const drafts: AutoDraft[] = picked.map(({ rule, src }) => {
    const proposal = proposePlan(state, { sourceKey: src.key, templateId: rule.templateId, now, ...(shared ? { busy: shared } : {}) });
    summaries.find((x) => x.ruleId === rule.id)!.drafts++;
    return { sourceKey: src.key, ruleId: rule.id, templateId: rule.templateId, proposal };
  });
  skipped.sort((a, b) => compareDates(a.date, b.date) || a.sourceKey.localeCompare(b.sourceKey));
  return { drafts, skipped, rules: summaries, truncated: candidates.length > picked.length, horizonDays: PREP_HORIZON_DAYS };
}

// ---------------------------------------------------------------------------
// Formkontroll av synkade/återställda dokument (opålitlig indata)
// ---------------------------------------------------------------------------

const isObj = (x: unknown): x is Record<string, unknown> => !!x && typeof x === 'object' && !Array.isArray(x);
const isStr = (x: unknown, max = 600): x is string => typeof x === 'string' && x.length > 0 && x.length <= max;
const isInt = (x: unknown): x is number => Number.isInteger(x);

/** Formkontroll för prepTemplate/prepRule/prepPlan från synk eller säkerhetskopia. null = godkänd. */
export function validatePrepDoc(entity: 'prepTemplate' | 'prepRule' | 'prepPlan', id: unknown, doc: unknown): string | null {
  if (!isObj(doc)) return 'Posten saknar innehåll.';
  if (doc['id'] !== id || !isStr(id, 100)) return 'id stämmer inte.';
  if (!isInt(doc['revision']) || (doc['revision'] as number) < 0) return 'Ogiltig revision.';
  if (entity === 'prepTemplate') {
    const issue = validatePrepTemplateContent(null, doc as unknown as PrepTemplateContent);
    return issue ? issue.message : null;
  }
  if (entity === 'prepRule') {
    if (!isStr(doc['sourceId'], 200) || !isStr(doc['templateId'], 100)) return 'Ogiltig regel.';
    if (doc['id'] !== prepRuleIdFor(doc['sourceId'] as string)) return 'Regelns id stämmer inte med källan.';
    if (doc['status'] !== 'active' && doc['status'] !== 'paused') return 'Ogiltig regelstatus.';
    return null;
  }
  if (!isStr(doc['sourceKey'], 600) || doc['id'] !== prepPlanIdFor(doc['sourceKey'] as string)) return 'Planens id stämmer inte med källposten.';
  if (doc['status'] !== 'active' && doc['status'] !== 'ended') return 'Ogiltig planstatus.';
  if (!isInt(doc['generation']) || (doc['generation'] as number) < 1) return 'Ogiltig generation.';
  if (!isObj(doc['snapshot']) || validatePrepTemplateContent(null, doc['snapshot'] as unknown as PrepTemplateContent)) return 'Ogiltig ögonblicksbild.';
  const basis = doc['basis'];
  if (!isObj(basis) || basis['sourceKey'] !== doc['sourceKey'] || !isStr(basis['deadlineStart'], 40) || !isStr(basis['deadlineDate'], 10) || typeof basis['allDay'] !== 'boolean' || !isStr(basis['sourceHash'], 128) || !isStr(basis['timeZone'], 64)) return 'Ogiltigt underlag.';
  const steps = doc['steps'];
  if (!Array.isArray(steps) || steps.length > PREP_MAX_STEPS) return 'Ogiltiga steg.';
  const seen = new Set<string>();
  for (const s of steps) {
    if (!isObj(s) || !isStr(s['stepId'], 64) || !isStr(s['eventId'], 100) || !isObj(s['approved']) || !isInt(s['instance'])) return 'Ogiltigt steg.';
    const ap = s['approved'];
    if (!isStr(ap['start'], 16) || !isStr(ap['end'], 16) || !isStr(ap['timeZone'], 64) || !isInt(ap['eventRevision'])) return 'Ogiltigt steg.';
    if (seen.has(s['eventId'] as string)) return 'Dubblerat pass.';
    seen.add(s['eventId'] as string);
  }
  if (doc['history'] !== undefined && (!Array.isArray(doc['history']) || (doc['history'] as unknown[]).length > 50)) return 'Ogiltig historik.';
  return null;
}

/** Tar bort formellt ogiltiga planposter ur opålitlig indata (säkerhetskopia) i stället för att krascha vyerna. */
export function sanitizePrepState(state: AppState): AppState {
  const clean = <T>(entity: 'prepTemplate' | 'prepRule' | 'prepPlan', coll: Record<string, T> | undefined): Record<string, T> => {
    const out: Record<string, T> = {};
    for (const [k, v] of Object.entries(coll ?? {})) if (!validatePrepDoc(entity, k, v)) out[k] = v;
    return out;
  };
  return { ...state, prepTemplates: clean('prepTemplate', state.prepTemplates), prepRules: clean('prepRule', state.prepRules), prepPlans: clean('prepPlan', state.prepPlans) };
}
